import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Avatar,
  Collapse,
} from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
// import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { defaultNodeCategories } from 'src/utils/knowlege-graph-api';
import Iconify from 'src/components/iconify';

const NodeDetails = ({ node, onClose, nodeCategories }) => {
  const [showSources, setShowSources] = useState(true);
  const categories = nodeCategories || defaultNodeCategories;
  const category = categories[node.category];
  const categoryColor = category?.color || '#ffffff';
  
  // Get progress/proficiency if available
  const progressValue = node.proficiency || node.progress || null;
  const progressLabel = node.proficiency ? 'Proficiency' : node.progress ? 'Progress' : null;
  
  // Get source messages
  const sourceMessages = node.sourceMessages || [];
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <Box
      sx={{
        width: 360,
        maxHeight: 'calc(100vh - 120px)',
        borderRadius: '20px',
        background: 'rgba(20, 10, 50, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeInUp 0.3s ease-out',
        '@keyframes fadeInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Header with gradient accent */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${categoryColor}20, transparent)`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Glow effect */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${categoryColor}15, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} display="flex" alignItems="center" gap={2}>
            {/* Avatar/Image for user/connection nodes */}
            {(node.image || node.category === 'user' || node.category === 'connection') && (
              <Avatar
                src={node.image}
                sx={{
                  width: 48,
                  height: 48,
                  border: `2px solid ${categoryColor}50`,
                  background: `linear-gradient(135deg, ${categoryColor}, ${categoryColor}80)`,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                }}
              >
                {!node.image && (node.name ? node.name.charAt(0).toUpperCase() : '?')}
              </Avatar>
            )}
            
            <Box>
              {/* Category badge */}
              <Box display="flex" alignItems="center" mb={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: categoryColor,
                    boxShadow: `0 0 10px ${categoryColor}`,
                    mr: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    color: categoryColor,
                    fontWeight: 600,
                  }}
                >
                  {category?.label || node.category || 'Node'}
                </Typography>
              </Box>
              
              {/* Node name */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: 'white',
                  lineHeight: 1.2,
                  fontSize: '1.1rem',
                }}
              >
                {node.name || node.id}
              </Typography>
            </Box>
          </Box>
          
          {/* Close button */}
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {/* <CloseIcon fontSize="small" /> */}
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto',
          p: 2.5,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '3px',
          },
        }}
      >
        {/* Description */}
        {node.description && (
          <Box mb={2.5}>
            <Typography 
              sx={{ 
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: 1.6,
              }}
            >
              {node.description}
            </Typography>
          </Box>
        )}

        {/* Email for users */}
        {node.email && (
          <Box mb={2}>
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
              }}
            >
              Email
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.8)',
                wordBreak: 'break-all',
              }}
            >
              {node.email}
            </Typography>
          </Box>
        )}
        
        {/* Frequency */}
        {node.frequency && (
          <Box mb={2}>
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
              }}
            >
              Mentioned
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: categoryColor,
              }}
            >
              {node.frequency} time{node.frequency > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
        
        {/* Progress bar if available */}
        {progressValue !== null && (
          <Box mb={2.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
              <Typography 
                sx={{ 
                  fontSize: '0.75rem',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {progressLabel}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: categoryColor,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {progressValue}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 6,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${categoryColor}, ${categoryColor}80)`,
                  boxShadow: `0 0 10px ${categoryColor}50`,
                },
              }}
            />
          </Box>
        )}
        
        {/* Activity for rooms */}
        {node.activity && (
          <Box mb={2.5}>
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
              }}
            >
              Activity
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: categoryColor,
              }}
            >
              {node.activity} messages
            </Typography>
          </Box>
        )}

        {/* Source Messages Section */}
        {sourceMessages.length > 0 && (
          <Box mb={2}>
            {/* Section Header - Clickable */}
            <Box
              onClick={() => setShowSources(!showSources)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 1,
                px: 1.5,
                mb: 1,
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.06)',
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {/* <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: categoryColor }} /> */}
                <Iconify
                    icon="ic:outline-chat-bubble-outline"
                    width={16}
                    style={{ color: categoryColor }}
                    />

                <Typography 
                  sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: 'white',
                  }}
                >
                  Source Messages
                </Typography>
                <Chip 
                  label={sourceMessages.length} 
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    background: `${categoryColor}30`,
                    color: categoryColor,
                  }}
                />
              </Box>
              {showSources ? (
                <>
                <Iconify
                    icon="ic:baseline-expand-less"
                    width={20}
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  />
                </>
              ) : (
                <>
                <Iconify
                  icon="ic:baseline-expand-more"
                  width={20}
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                  />
                </>
                // <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.5)' }} />
              )}
            </Box>
            
            {/* Collapsible Source Messages */}
            <Collapse in={showSources}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {sourceMessages.map((msg, index) => (
                  <Box
                    key={msg.messageId || index}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        borderColor: `${categoryColor}30`,
                      },
                    }}
                  >
                    {/* Room Name */}
                    {msg.roomName && (
                      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                        {/* <MeetingRoomIcon sx={{ fontSize: 14, color: '#4d6cff' }} /> */}
                        <Iconify
                          icon="ic:baseline-meeting-room"
                          width={14}
                          style={{ color: '#4d6cff' }}
                        />

                        <Typography 
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#4d6cff',
                          }}
                        >
                          {msg.roomName}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Message Content */}
                    <Typography 
                      sx={{ 
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        lineHeight: 1.5,
                        mb: 1,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {truncateText(msg.content || msg.textRepresentation, 200)}
                    </Typography>
                    
                    {/* Timestamp */}
                    {msg.createdAt && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {/* <AccessTimeIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }} /> */}
                        <Iconify
                          icon="ic:baseline-access-time"
                          width={12}
                          style={{ color: 'rgba(255,255,255,0.4)' }}
                          />

                        <Typography 
                          sx={{ 
                            fontSize: '0.7rem',
                            color: 'rgba(255, 255, 255, 0.4)',
                          }}
                        >
                          {formatDate(msg.createdAt)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Metadata */}
        <Box 
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.5,
            pt: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.03)',
            }}
          >
            <Typography 
              sx={{ 
                fontSize: '0.65rem',
                color: 'rgba(255, 255, 255, 0.4)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
              }}
            >
              ID
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.7rem',
                fontFamily: "'JetBrains Mono', monospace",
                color: 'rgba(255, 255, 255, 0.8)',
                wordBreak: 'break-all',
              }}
            >
              {node.id.length > 20 ? `${node.id.slice(0, 18)}...` : node.id}
            </Typography>
          </Box>
          
          <Box
            sx={{
              p: 1.5,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.03)',
            }}
          >
            <Typography 
              sx={{ 
                fontSize: '0.65rem',
                color: 'rgba(255, 255, 255, 0.4)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
              }}
            >
              Weight
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.8rem',
                fontFamily: "'JetBrains Mono', monospace",
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              {node.val || 1}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NodeDetails;
